import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import Logo from "../assets/logo.png";
import { AuthContext } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

const CategoryProductsPage = () => {
  const { categoryId } = useParams();
  const { user } = useContext(AuthContext);
  const { addToCart, removeFromCart, updateQuantity, isInCart, getItemQuantity } = useCart();
  const navigate = useNavigate();

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(categoryId || null);
  const [error, setError] = useState(null);

  const initialLoadRef = useRef(false);
  const abortControllerRef = useRef(null);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get('/category');
      const active = res.data.filter(c => c.isActive);
      setCategories(active);
      return active;
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
      return [];
    }
  }, []);

  const fetchProductsByCategory = useCallback(async (catId, allCategories, subcatName = null) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    setLoading(true);
    setError(null);
    try {
      const params = subcatName ? { subcategoryId: subcatName } : {};
      const res = await axios.get(`/product/category/${catId}`, {
        signal: abortControllerRef.current.signal,
        params
      });
      setProducts(res.data.products || []);
      const cat = allCategories.find(c => c._id === catId || c.id === catId);
      setSelectedCategory(cat);
      if (subcatName) {
        setSelectedSubcategory(cat?.subcategories?.find(s => s.name === subcatName));
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error fetching products:', err);
        setError('Failed to load products');
        setProducts([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllProducts = useCallback(async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/product/available', {
        signal: abortControllerRef.current.signal,
      });
      setProducts(res.data.products || []);
      setSelectedCategory(null);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error fetching all products:', err);
        setError('Failed to load products');
        setProducts([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCategorySelect = useCallback(
    async (catId) => {
      setActiveCategory(catId);
      setSelectedSubcategory(null);
      setLoading(false);
      if (catId) {
        // Find and set the selected category
        const cat = categories.find(c => c._id === catId || c.id === catId);
        setSelectedCategory(cat);
      } else {
        setSelectedCategory(null);
        await fetchAllProducts();
      }
    },
    [categories, fetchAllProducts]
  );

  const handleSubcategorySelect = useCallback(
    async (catId, subcatName, allCategories) => {
      await fetchProductsByCategory(catId, allCategories, subcatName);
    },
    [fetchProductsByCategory]
  );

  useEffect(() => {
    const init = async () => {
      if (initialLoadRef.current) return;
      initialLoadRef.current = true;
      const allCats = await fetchCategories();
      if (categoryId) {
        const cat = allCats.find(c => c._id === categoryId || c.id === categoryId);
        setSelectedCategory(cat);
        setLoading(false); // Don't load products yet
      } else {
        await fetchAllProducts();
      }
    };
    init();
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [categoryId, fetchCategories, fetchAllProducts]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        /* ── Reset & base ── */
        .cpp-root * { box-sizing: border-box; }
        .cpp-root {
          font-family: 'Inter', sans-serif;
          display: flex;
          height: 100vh;
          overflow: hidden;
          background: #f8fafc;
        }

        /* ══════════════════════════════════════════
           SIDEBAR — fixed 20 % on every screen size
        ══════════════════════════════════════════ */
        .cpp-sidebar {
          width: 20%;
          min-width: 68px;      /* prevents collapse on tiny phones */
          max-width: 280px;
          background: #ffffff;
          border-right: 1px solid rgba(99,102,241,0.12);
          box-shadow: 4px 0 24px rgba(0,0,0,0.06);
          display: flex;
          flex-direction: column;
          height: 100vh;
          position: sticky;
          top: 0;
          flex-shrink: 0;
        }

        .cpp-sidebar-header {
          // padding: 18px 12px 14px;
          background: #05F1FF;
          color: #fff;
          flex-shrink: 0;
        }

        .cpp-sidebar-header h2 {
          font-size: clamp(12px, 1.4vw, 18px);
          font-weight: 700;
          margin: 0 0 3px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .cpp-sidebar-header p {
          font-size: clamp(10px, 1vw, 13px);
          opacity: 0.85;
          margin: 0;
        }

        /* Vertical scrollable list — the "slider" */
        .cpp-cat-list {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 10px 8px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          scrollbar-width: thin;
          scrollbar-color: rgba(99,102,241,0.2) transparent;
        }
        .cpp-cat-list::-webkit-scrollbar { width: 4px; }
        .cpp-cat-list::-webkit-scrollbar-thumb {
          background: rgba(99,102,241,0.25);
          border-radius: 99px;
        }

        .cpp-cat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 5px;
          padding: 10px 6px;
          border-radius: 14px;
          cursor: pointer;
          transition: background 0.2s, transform 0.2s;
          flex-shrink: 0;
        }
        .cpp-cat-item:hover {
          background: rgba(99,102,241,0.07);
          transform: scale(1.03);
        }
        .cpp-cat-item.active {
          background: rgba(99,102,241,0.12);
          border: 1px solid rgba(99,102,241,0.35);
        }

        .cpp-cat-icon {
          width: clamp(34px, 3.5vw, 46px);
          height: clamp(34px, 3.5vw, 46px);
          border-radius: 12px;
          background: #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          flex-shrink: 0;
          box-shadow: 0 3px 10px rgba(0,0,0,0.08);
        }
        .cpp-cat-item.active .cpp-cat-icon {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
        }
        .cpp-cat-icon img {
          width: 65%;
          height: 65%;
          object-fit: cover;
        }
        .cpp-cat-item.active .cpp-cat-icon img {
          filter: brightness(0) invert(1);
        }

        .cpp-cat-name {
          font-size: clamp(9px, 0.95vw, 12.5px);
          font-weight: 600;
          color: #334155;
          line-height: 1.3;
          word-break: break-word;
        }
        .cpp-cat-item.active .cpp-cat-name { color: #4f46e5; }

        .cpp-cat-count {
          font-size: clamp(8px, 0.75vw, 11px);
          color: #94a3b8;
        }

        /* ══════════════════════════════════════════
           PRODUCTS AREA — 80 %
        ══════════════════════════════════════════ */
        .cpp-products-area {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          min-width: 0;
        }

        .cpp-products-header {
          padding: 20px 28px 16px;
          border-bottom: 1px solid rgba(99,102,241,0.1);
          flex-shrink: 0;
          background: #fff;
        }

        .cpp-products-header h1 {
          font-size: clamp(20px, 3vw, 36px);
          font-weight: 800;
          background: linear-gradient(135deg, #1e293b, #475569);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin: 0 0 4px;
        }

        .cpp-products-header p {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        .cpp-grid-scroll {
          flex: 1;
          overflow-y: auto;
          padding: 24px 24px 32px;
        }

        .cpp-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(clamp(140px, 18%, 220px), 1fr));
          gap: 20px;
        }

        /* ── Product card ── */
        .cpp-card {
          background: #ffffff;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 6px 20px -6px rgba(0,0,0,0.1);
          cursor: pointer;
          transition: transform 0.35s ease, box-shadow 0.35s ease;
        }
        .cpp-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 14px 30px -8px rgba(99,102,241,0.2);
        }

        .cpp-img-wrap {
          position: relative;
          aspect-ratio: 1;
          overflow: hidden;
          background: #f8fafc;
        }
        .cpp-img-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        .cpp-card:hover .cpp-img-wrap img { transform: scale(1.08); }

        .cpp-badge {
          position: absolute;
          top: 10px;
          left: 10px;
          background: #ef4444;
          color: #fff;
          padding: 3px 10px;
          border-radius: 99px;
          font-size: 10px;
          font-weight: 700;
        }

        .cpp-info {
          padding: 14px 16px 16px;
        }
        .cpp-tag {
          display: inline-block;
          font-size: 10.5px;
          background: #f1f5f9;
          color: #475569;
          padding: 3px 9px;
          border-radius: 99px;
          margin-bottom: 7px;
        }
        .cpp-name {
          font-size: clamp(12px, 1.1vw, 14.5px);
          font-weight: 700;
          color: #1e293b;
          line-height: 1.4;
          margin-bottom: 8px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .cpp-price {
          font-size: clamp(14px, 1.4vw, 18px);
          font-weight: 800;
          color: #6366f1;
        }
        .cpp-old {
          font-size: 12px;
          color: #94a3b8;
          text-decoration: line-through;
          margin-left: 5px;
          font-weight: 400;
        }
        .cpp-buy {
          display: block;
          width: 100%;
          margin-top: 12px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: #fff;
          border: none;
          padding: 10px;
          border-radius: 12px;
          font-size: clamp(11px, 1vw, 13px);
          font-weight: 700;
          cursor: pointer;
          transition: box-shadow 0.2s;
        }
        .cpp-buy:hover {
          box-shadow: 0 6px 18px rgba(99,102,241,0.4);
        }

        /* ── States ── */
        .cpp-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          color: #64748b;
          text-align: center;
        }

        @keyframes cpp-spin {
          to { transform: rotate(360deg); }
        }
        .cpp-spinner {
          width: 44px;
          height: 44px;
          border: 4px solid #e2e8f0;
          border-top-color: #6366f1;
          border-radius: 50%;
          margin-bottom: 16px;
          animation: cpp-spin 0.9s linear infinite;
        }

        .cpp-retry-btn {
          margin-top: 16px;
          padding: 10px 26px;
          background: #6366f1;
          color: #fff;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
        }

        /* ── Subcategories list ── */
        .cpp-subcat-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .cpp-subcat-item {
          background: #ffffff;
          border-radius: 12px;
          padding: 14px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          box-shadow: 0 2px 10px -2px rgba(0,0,0,0.08);
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .cpp-subcat-item:hover {
          transform: translateX(4px);
          box-shadow: 0 4px 15px -2px rgba(99,102,241,0.15);
        }
        .cpp-subcat-left {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .cpp-subcat-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: linear-gradient(135deg, rgba(99,102,241,0.1), rgba(139,92,246,0.1));
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cpp-subcat-icon svg {
          width: 55%;
          height: 55%;
          stroke: #6366f1;
        }
        .cpp-subcat-name {
          font-size: 15px;
          font-weight: 600;
          color: #1e293b;
        }
        .cpp-subcat-chevron svg {
          width: 20px;
          height: 20px;
          stroke: #94a3b8;
        }

        /* ── All-icon SVG placeholder ── */
        .cpp-all-icon svg {
          width: 55%;
          height: 55%;
          stroke: #94a3b8;
        }
        .cpp-cat-item.active .cpp-all-icon svg {
          stroke: #fff;
        }

        .product-price {
          font-size: 24px;
          font-weight: 800;
          color: #6366f1;
          display: flex;
          align-items: baseline;
          gap: 10px;
          flex-wrap: wrap;
        }

        .old-price {
          font-size: 15px;
          color: black;
          // text-decoration: line-through;
          font-weight: 600;
        }

      `}</style>

      <div className="cpp-root">
        {/* ── Sidebar ── */}
        <aside className="cpp-sidebar">
          <div className="cpp-sidebar-header">
            <img src={Logo} style={{ width: '100%', height: 'auto', padding: '4px' }} alt="Sidebar" />
          </div>

          <div className="cpp-cat-list">
            {/* All Products */}
            <div
              className={`cpp-cat-item ${!activeCategory ? 'active' : ''}`}
              onClick={() => handleCategorySelect(null)}
            >
              <div className="cpp-cat-icon cpp-all-icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M3 9h18M9 21V9" />
                </svg>
              </div>
              <span className="cpp-cat-name">All</span>
              <span className="cpp-cat-count">All items</span>
            </div>

            {categories.map((cat) => (
              <div
                key={cat._id}
                className={`cpp-cat-item `}
                onClick={() => handleCategorySelect(cat._id)}
              >
                <div className="cpp-cat-icon">
                  <img src={cat.image} alt={cat.name} />
                </div>
                <span className="cpp-cat-name">{cat.name}</span>
                {/* <span className="cpp-cat-count">{cat.productCount ?? 0}</span> */}
              </div>
            ))}
          </div>
        </aside>

        {/* ── Products / Subcategories ── */}
        <main className="cpp-products-area">
          <div className="cpp-products-header">
            <h1>
              {selectedSubcategory
                ? selectedSubcategory.name
                : selectedCategory
                  ? selectedCategory.name
                  : 'All Products'}
            </h1>
            <p>
              {selectedSubcategory
                ? `Best ${selectedSubcategory.name} in ${selectedCategory.name}`
                : selectedCategory
                  ? `Choose a subcategory in ${selectedCategory.name}`
                  : 'Discover our premium selection'}
            </p>
          </div>

          <div className="cpp-grid-scroll">
            {loading ? (
              <div className="cpp-state">
                <div className="cpp-spinner" />
                <p>Loading…</p>
              </div>
            ) : error ? (
              <div className="cpp-state">
                <p style={{ color: '#dc2626', fontWeight: 600 }}>{error}</p>
                <button
                  className="cpp-retry-btn"
                  onClick={() => window.location.reload()}
                >
                  Retry
                </button>
              </div>
            ) : selectedCategory && !selectedSubcategory ? (
              // Show subcategories
              <div className="cpp-subcat-list">
                {(selectedCategory.subcategories || []).map((subcat, idx) => {
                  // Product-related icons for subcategories
                  const subcatIcons = [
                    // Product Box
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" key="1">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                      <polyline points="3.29 7 12 12 20.71 7" />
                      <line x1="12" y1="22" x2="12" y2="12" />
                    </svg>,

                    // Shopping Bag
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" key="2">
                      <path d="M6 2l1.5 4h9L18 2" />
                      <path d="M3 6h18l-1.5 14a2 2 0 0 1-2 2H6.5a2 2 0 0 1-2-2L3 6z" />
                    </svg>,

                    // Shopping Cart
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" key="3">
                      <circle cx="9" cy="21" r="1" />
                      <circle cx="20" cy="21" r="1" />
                      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                    </svg>,

                    // Tag / Price Tag
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" key="4">
                      <path d="M20.59 13.41L11 3H4v7l9.59 9.59a2 2 0 0 0 2.82 0l4.18-4.18a2 2 0 0 0 0-2.82z" />
                      <line x1="7" y1="7" x2="7.01" y2="7" />
                    </svg>,

                    // Gift Box
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" key="5">
                      <polyline points="20 12 20 22 4 22 4 12" />
                      <rect x="2" y="7" width="20" height="5" />
                      <line x1="12" y1="22" x2="12" y2="7" />
                      <path d="M12 7H7.5a2.5 2.5 0 1 1 0-5C10 2 12 7 12 7z" />
                      <path d="M12 7h4.5a2.5 2.5 0 1 0 0-5C14 2 12 7 12 7z" />
                    </svg>,

                    // Store
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" key="6">
                      <path d="M3 9l2-6h14l2 6" />
                      <path d="M4 9h16v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9z" />
                      <line x1="9" y1="22" x2="9" y2="12" />
                    </svg>
                  ];
                  return (
                    <div
                      key={idx}
                      className="cpp-subcat-item"
                      onClick={() => handleSubcategorySelect(selectedCategory._id, subcat.name, categories)}
                    >
                      <div className="cpp-subcat-left">
                        <div className="cpp-subcat-icon">
                          {subcatIcons[idx % subcatIcons.length]}
                        </div>
                        <span className="cpp-subcat-name">{subcat.name}</span>
                      </div>
                      <div className="cpp-subcat-chevron">
                        <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </div>
                    </div>
                  );
                })}
                {(!selectedCategory.subcategories || selectedCategory.subcategories.length === 0) && (
                  <div className="cpp-state" style={{ marginTop: 20 }}>
                    <p>No subcategories found in this category.</p>
                  </div>
                )}
              </div>
            ) : (
              // Show products
              products.length === 0 ? (
                <div className="cpp-state">
                  <p>No products found.</p>
                </div>
              ) : (
                <div className="cpp-grid">
                  {products.map((product) => {
                    const inCart = isInCart(product._id);
                    const cartQuantity = getItemQuantity(product._id);
                    return (
                      <div
                        key={product._id}
                        className="cpp-card"
                      >
                        <div className="cpp-img-wrap">
                          <img
                            src={
                              product.images?.[0] ||
                              'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600'
                            }
                            alt={product.name}
                            loading="lazy"
                          />
                          {product.quantity > 0 && product.quantity <= 10 && (
                            <div className="cpp-badge">Only {product.quantity} left</div>
                          )}
                        </div>

                        <div className="cpp-info">
                          <h3 className="cpp-name">{product.name}</h3>
                          <div className="product-price">
                            <span className="old-price">MRP: ₹{(product?.mrp || 0).toLocaleString()}</span>
                            {user && (
                              <span className="old-price">SP: ₹{(product?.sp || 0).toLocaleString()}</span>
                            )}
                            {user && (
                              <span className="old-price">PV: ₹{(product?.pv || 0).toLocaleString()}</span>
                            )}
                          </div>
                          {inCart ? (
                            <div className="flex items-center gap-2 mt-2">
                              <button
                                className="w-8 h-8 bg-white border border-gray-300 rounded-lg flex items-center justify-center font-semibold hover:bg-gray-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateQuantity(product._id, cartQuantity - 1);
                                }}
                              >
                                −
                              </button>
                              <span className="w-8 text-center font-bold">{cartQuantity}</span>
                              <button
                                className="w-8 h-8 bg-white border border-gray-300 rounded-lg flex items-center justify-center font-semibold hover:bg-gray-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addToCart(product._id, product.name);
                                }}
                              >
                                +
                              </button>
                            </div>
                          ) : (
                            <button
                              className="cpp-buy"
                              onClick={(e) => {
                                e.stopPropagation();
                                addToCart(product._id, product.name);
                              }}
                            >
                              🛒 Add to Cart
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default CategoryProductsPage;